import * as React from 'react';
import {FiChevronLeft, FiChevronRight, FiMoreHorizontal} from 'react-icons/fi';
import Link from 'next/link';

import {buttonVariants} from '@/modules/foundation/components/button';
import {cn} from '@/modules/foundation/shared/tailwind';

const Pagination = ({className, ...props}: React.ComponentProps<'nav'>) => (
	<nav
		role="navigation"
		aria-label="pagination"
		className={cn('mx-auto flex w-full justify-center', className)}
		{...props}
	/>
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<
	HTMLUListElement,
	React.ComponentProps<'ul'>
>(({className, ...props}, ref) => (
	<ul
		ref={ref}
		className={cn('flex flex-row items-center gap-1', className)}
		{...props}
	/>
));
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<
	HTMLLIElement,
	React.ComponentProps<'li'>
>(({className, ...props}, ref) => (
	<li ref={ref} className={cn('', className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
	isActive?: boolean;
} & React.ComponentProps<'a'>;

const PaginationLink = ({
	className,
	isActive,
	href,
	...props
}: PaginationLinkProps) => (
	<Link
		aria-current={isActive ? 'page' : undefined}
		className={cn(
			buttonVariants({
				variant: isActive ? 'active' : 'secondary',
			}),
			'size-[34px]!',
			'p-2!',
			'rounded-[3px]!',
			className,
		)}
		href={href ?? ''}
		{...props}
	/>
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({
	className,
	...props
}: React.ComponentProps<typeof PaginationLink>) => (
	<PaginationLink
		aria-label="Gå til forrige side"
		className={cn(className)}
		{...props}
	>
		<FiChevronLeft aria-hidden className="size-4" />
		<span className="sr-only">Forrige</span>
	</PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({
	className,
	...props
}: React.ComponentProps<typeof PaginationLink>) => (
	<PaginationLink
		aria-label="Gå til neste side"
		className={cn(className)}
		{...props}
	>
		<span className="sr-only">Neste</span>
		<FiChevronRight className="size-4" aria-hidden />
	</PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({
	className,
	...props
}: React.ComponentProps<'span'>) => (
	<span
		aria-hidden
		className={cn('flex h-9 w-9 items-center justify-center', className)}
		{...props}
	>
		<FiMoreHorizontal className="size-4" />
		<span className="sr-only">Flere sider</span>
	</span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
};
